import type { ReactNode } from 'react'

import type { BaseStep, GetExtendedStateRef } from '../useStepHandlers'

type RenderResult = {
	content: ReactNode
	showHeader?: boolean
	showFooter?: boolean
}

type RenderHelpers<TStep extends BaseStep> = {
	getExtendedStateRef?: GetExtendedStateRef<TStep>
}

export type StepRenderers<TStep extends BaseStep> = {
	[TType in TStep['type']]: (
		step: TaggedType<TStep, TType>,
		helpers: RenderHelpers<TaggedType<TStep, TType>>,
	) => RenderResult | ReactNode
}

export const getRenderResult = <TStep extends BaseStep>(
	step: TStep | undefined,
	renderers: StepRenderers<TStep>,
	getExtendedStateRef?: GetExtendedStateRef<TStep>,
): RenderResult => {
	if (!step) return { content: undefined }

	const renderer = renderers[step.type as TStep['type']]

	if (!renderer) {
		throw new Error(`StepWizard renderer for '${step.type}' is missing`)
	}

	const result = renderer(step as any, { getExtendedStateRef: getExtendedStateRef as any })

	if (!result) {
		throw new Error(`StepWizard renderer for '${step.type}' returned ${result}`)
	}

	if (typeof result === 'object' && 'content' in result) {
		return result
	}
	return { content: result }
}
